import { LinearProgress, List, ListItemButton, ListItemText } from '@mui/material';
import { useFetchStatementsQuery } from '@api/Trinium/Endpoints/Settlements/slice';
import { SettlementStatementRecord } from '@api/Trinium/Endpoints/Settlements/types';
import ButtonDropdown, { PopupContentProps } from '@forms/Components/Dropdowns/ButtonDropdown';
import useCurrentTerminal from '@hooks/useCurrentTerminal';

import styles from './StatementSelector.module.css';

export interface StatementSelectorProps {
  value: number | null;
  onChange: (statementId: number) => void;
}

export default function StatementSelector({ value, onChange }: StatementSelectorProps) {
  const { number: terminalNumber } = useCurrentTerminal();
  const { data: statements, isFetching, isError } = useFetchStatementsQuery({ terminalNumber });

  const selected = statements?.find((statement) => statement.statementId === value) ?? null;

  return (
    <ButtonDropdown
      className={styles.selector}
      label='Statement'
      ButtonContent={StatementButtonContent}
      buttonContentProps={{ statement: selected }}
      PopupContent={StatementListContent}
      popupContentProps={{ statements, isFetching, isError }}
      fieldOnChange={(selectedValue) => {
        const statement = selectedValue as SettlementStatementRecord | null;
        if (statement) {
          onChange(statement.statementId);
        }
      }}
    />
  );
}

function billingWeekLabel(statement: SettlementStatementRecord) {
  return `(Billing Week ${statement.billingWeek})`;
}

function StatementButtonContent({ statement }: { statement: SettlementStatementRecord | null }) {
  if (!statement) {
    return <span className={styles['button-primary']}>Select a Statement</span>;
  }

  return (
    <div className={styles['button-content']}>
      <span className={styles['button-primary']}>{statement.statementDate.shortDateExtended}</span>
      <span className={styles['button-week']}>{billingWeekLabel(statement)}</span>
    </div>
  );
}

function StatementListContent({
  onChange,
  statements,
  isFetching,
  isError
}: PopupContentProps & {
  statements?: SettlementStatementRecord[];
  isFetching: boolean;
  isError: boolean;
}) {
  if (isFetching) {
    return (
      <div className={styles.popup}>
        <LinearProgress />
      </div>
    );
  }

  if (isError) {
    return <div className={styles.empty}>Statements could not be loaded. Try again.</div>;
  }

  if (!statements?.length) {
    return <div className={styles.empty}>No statements available.</div>;
  }

  return (
    <List
      disablePadding
      dense
      className={styles.popup}>
      {statements.map((statement) => (
        <ListItemButton
          key={statement.statementId}
          onClick={() => {
            onChange(statement);
          }}>
          <ListItemText
            primary={statement.statementDate.shortDateExtended}
            secondary={<span className={styles['popup-week']}>{billingWeekLabel(statement)}</span>}
          />
        </ListItemButton>
      ))}
    </List>
  );
}
